import { useEffect, useState } from "react";
import { CodeBlock, JsonBlock } from "@/components/CodeBlock";
import { FileTreeDiagram, SizeSum } from "@/components/FileTreeDiagram";
import { ByteView, Field, FieldGroup } from "@/components/HashDisplay";
import { Prop } from "@/components/Prop";
import { toHex } from "@/hashtree/bytes";
import { decodeNode } from "@/hashtree/msgpack";
import { buildDirectoryNode, buildFileNode, planDirectory, type BuiltNode } from "@/hashtree/nodes";
import type { HashtreeBuild } from "@/hashtree/useHashtreeBuild";
import { Slide, SpecNote, Step } from "./Slide";

/** Render a node's links the way the spec's "decoded form" examples do. */
function decodedForm(node: BuiltNode) {
  const parsed = decodeNode(node.plaintext);
  return {
    l: parsed.l.map((link) => ({
      h: toHex(link.h),
      ...(link.k ? { k: toHex(link.k) } : {}),
      ...(link.m ? { m: link.m } : {}),
      ...(link.n !== undefined ? { n: link.n } : {}),
      s: link.s,
      t: link.t,
    })),
    t: parsed.t,
  };
}

export function FileManifestSlide({ build, onNext }: { build: HashtreeBuild; onNext: () => void }) {
  const { tree, geometry } = build;
  if (!tree || !geometry) return null;

  const file = tree.file;
  const isSingleBlob = file.kind === "blob";
  // Nesting means at least one link points at another manifest rather than a chunk.
  const nested = !isSingleBlob && file.children.some((child) => child.kind !== "blob");

  return (
    <Slide
      bud={17}
      title="The file manifest"
      lede={
        isSingleBlob ? (
          <>
            Your file fits in one chunk, so BUD-17 says to skip the manifest entirely and publish it as a plain
            <Prop name="t" value={0} /> blob. Lower the chunk size on the previous slide to see a real{" "}
            <Prop name="t" value={1} /> manifest.
          </>
        ) : (
          <>
            A file manifest is a list of its chunks in order. It uses the same MessagePack node format as a directory —
            only <Prop name="t" /> and the absence of names distinguish them.
          </>
        )
      }
    >
      {isSingleBlob ? (
        <FieldGroup>
          <Field label="blob hash" value={toHex(file.encrypted.blobHash)} tone="hash" />
          <Field label="chk key" value={toHex(file.encrypted.chkKey)} tone="key" />
          <Field
            label={<Prop name="t" value={0} />}
            value="raw blob, which has no node type of its own"
            copyable={false}
          />
        </FieldGroup>
      ) : (
        <>
          <Step n={1} title={nested ? "The manifests, nested" : "The manifest and its chunks"}>
            {nested && (
              <SpecNote tone="warn" title={`More than max_links = ${build.maxLinks} chunks`}>
                One node cannot hold them all, so consecutive links were grouped into child{" "}
                <Prop name="t" value={1} /> manifests and a new parent built over those. That repeats until the root
                holds at most <code>max_links</code> links. Click any node to see its hash and key.
              </SpecNote>
            )}

            <FileTreeDiagram root={file} fileName={build.file?.name ?? "file"} />

            <div className="space-y-1 pt-1">
              <p className="text-xs text-muted-foreground">
                The root's <Prop name="s" /> values sum to the plaintext file size, which is how a reader knows the
                length from the root alone:
              </p>
              <SizeSum node={file} />
            </div>

            <details className="rounded-lg border bg-card">
              <summary className="cursor-pointer px-3 py-2 text-xs font-medium text-muted-foreground">
                The same node as decoded JSON
              </summary>
              <div className="border-t p-3">
                <JsonBlock value={decodedForm(file)} caption={`decoded t=${file.nodeType} root node`} />
              </div>
            </details>
          </Step>

          <Step n={2} title="Encoded deterministically">
            <ByteView bytes={file.plaintext} label="MessagePack bytes" />
            <SpecNote>
              Field order is fixed at{" "}
              <Prop name="h" />, <Prop name="k" />, <Prop name="m" />, <Prop name="n" />, <Prop name="s" />,{" "}
              <Prop name="t" />, integers use their shortest form, and hashes use
              binary types rather than hex strings. Any implementation following these rules produces these exact bytes.
            </SpecNote>
          </Step>

          <Step n={3} title="Then encrypted like any other blob">
            <FieldGroup>
              <Field
                label="blob hash"
                value={toHex(file.encrypted.blobHash)}
                tone="hash"
                hint="The manifest is itself a CHK blob — it gets uploaded exactly like a chunk does."
              />
              <Field label="chk key" value={toHex(file.encrypted.chkKey)} tone="key" />
              <Field
                label="total size"
                value={`${file.size} bytes — the sum of the link sizes, which is the plaintext file size`}
                copyable={false}
              />
            </FieldGroup>
          </Step>

          <SpecNote tone="warn">
            File manifest links MUST NOT carry <Prop name="n" />. A name would make it a directory entry; chunks are
            positional, ordered by byte offset.
          </SpecNote>
        </>
      )}

      <FanoutSandbox maxLinks={build.maxLinks} />

      <button
        type="button"
        onClick={onNext}
        className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90"
      >
        Put it in a folder
      </button>
    </Slide>
  );
}

/**
 * Fanout cannot occur in a root → folder → file tree, because every directory
 * holds exactly one entry. This sandbox builds a real `t = 3` node from
 * placeholder entries so the user can see one without changing their tree.
 */
function FanoutSandbox({ maxLinks }: { maxLinks: number }) {
  const [entryCount, setEntryCount] = useState(5);
  const [sandboxLinks, setSandboxLinks] = useState(2);
  const [node, setNode] = useState<BuiltNode | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const entries = [];
        for (let i = 0; i < entryCount; i++) {
          const name = `${String.fromCharCode(97 + (i % 26))}${i >= 26 ? Math.floor(i / 26) : ""}.txt`;
          const bytes = new TextEncoder().encode(`contents of ${name}`);
          entries.push({ name, node: await buildFileNode(bytes, name, { chunkSize: 1024, maxLinks: 174 }) });
        }
        const built = await buildDirectoryNode(entries, "sandbox", { maxLinks: sandboxLinks });
        if (!cancelled) {
          setNode(built);
          setError(null);
        }
      } catch (problem) {
        if (!cancelled) setError(problem instanceof Error ? problem.message : String(problem));
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [entryCount, sandboxLinks]);

  const shape = planDirectory(entryCount, { maxLinks: sandboxLinks });

  return (
    <details className="rounded-lg border bg-card">
      <summary className="cursor-pointer px-4 py-3 text-sm font-medium">
        Sandbox: what a <Prop name="t" value={3} /> fanout node looks like
      </summary>
      <div className="space-y-3 border-t px-4 py-3">
        <p className="text-xs text-muted-foreground">
          Your tree has one entry per folder, so it will never fan out — with the canonical{" "}
          <code>max_links = {maxLinks}</code> a directory needs more than {maxLinks} entries before it does. This
          sandbox builds a real fanout from placeholder files. It does not touch your tree.
        </p>

        <div className="flex flex-wrap gap-4">
          <label className="flex items-center gap-2 text-xs">
            entries
            <input
              type="number"
              min={1}
              max={40}
              value={entryCount}
              onChange={(event) => setEntryCount(Math.max(1, Math.min(40, Number(event.target.value) || 1)))}
              className="w-20 rounded-md border bg-background px-2 py-1 font-mono tabular-nums"
            />
          </label>
          <label className="flex items-center gap-2 text-xs">
            max_links
            <input
              type="number"
              min={2}
              max={20}
              value={sandboxLinks}
              onChange={(event) => setSandboxLinks(Math.max(2, Math.min(20, Number(event.target.value) || 2)))}
              className="w-20 rounded-md border bg-background px-2 py-1 font-mono tabular-nums"
            />
          </label>
        </div>

        <p className="text-xs">
          {shape.kind === "flat-directory" ? (
            <>
              {entryCount} entries fit in one <Prop name="t" value={2} /> directory — no fanout needed.
            </>
          ) : (
            <>
              {entryCount} entries split into {shape.childCount} child directories under fanout nodes, {shape.depth}{" "}
              levels above the entries.
            </>
          )}
        </p>

        {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}

        {node && (
          <>
            <JsonBlock value={decodedForm(node)} caption={`decoded t=${node.nodeType} root`} />
            {node.kind === "fanout" && (
              <SpecNote>
                Fanout links omit <Prop name="n" /> and carry <Prop name="count" />, <Prop name="first" /> and <Prop name="last" />{" "}
                instead. A reader uses those bounds to pick the one child that could contain the name it wants, rather
                than fetching them all. Readers must flatten these nodes — they are never shown as directory entries.
              </SpecNote>
            )}
          </>
        )}

        <CodeBlock
          caption="BUD-17 directory splitting"
          code={`1. Sort entries bytewise by name.
2. At most max_links entries? Publish one t=2 directory manifest.
3. Otherwise split into consecutive batches of max_links entries.
4. One child t=2 directory manifest per batch.
5. Parent t=3 fanout node, one unnamed link per child.
6. Each link carries m.count, m.first, m.last.
7. Too many fanout links? Group those into t=3 nodes too, and repeat.`}
        />
      </div>
    </details>
  );
}
